import { useCallback, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { ACTIVITY_TYPES, isSingleEventActivity, type ActivityType } from 'shared';
import { useActivities, useDeleteActivity, type Activity } from '@/hooks/useActivities';
import ActivityModal from '@/components/activities/ActivityModal';
import {
  ConfirmDialog,
  ErrorBlock,
  LoadingBlock,
  TypeChip,
  UniversoChip,
} from '@/components/activities/ActivityUi';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import { formatSessionDate } from '@/lib/activityTime';

const PAGE_SIZE = 20;

// Plural labels for the filter; ACTIVITY_TYPE_LABELS holds the singular.
const FILTER_LABELS: Record<ActivityType | '', string> = {
  '': 'Todas',
  club: 'Clubes',
  circuit: 'Circuitos',
  open_day: 'Dias Abertos',
};

/**
 * When an activity is next on. An open day is one date, which it keeps showing
 * once it is over; the others show the session coming up.
 */
function whenIsIt(activity: Activity): string {
  const at = isSingleEventActivity(activity.type) ? activity.lastSessionAt : activity.nextSessionAt;
  return at ? formatSessionDate(at) : '—';
}

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; activity: Activity }
  | { type: 'delete'; activity: Activity };

export default function AtividadesPage() {
  const navigate = useNavigate();
  const { toasts, toast, dismiss } = useToast();
  const del = useDeleteActivity();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState<ActivityType | ''>('');
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const { data, isLoading, isError, error, isFetching } = useActivities({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
    type: filterType,
  });

  const activities = data?.activities ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);
  const hasFilters = !!debouncedSearch || filterType !== '';

  const closeModal = () => setModal({ type: 'none' });

  const confirmDelete = async (activity: Activity) => {
    try {
      await del.mutateAsync(activity.id);
      toast.success(`"${activity.name}" arquivada com sucesso.`);
      closeModal();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Erro ao arquivar.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Atividades | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-primary" />
              Atividades
            </h1>
            <p className="text-muted-foreground mt-1">
              Clubes, Circuitos e Dias Abertos. Cada sessão, e cada Dia Aberto, tem um código QR
              que regista a presença de quem o lê.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModal({ type: 'create' })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm shrink-0"
          >
            <Plus className="w-4 h-4" />
            Nova Atividade
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="Pesquisar por nome…"
              className="input pl-9 w-full"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label="Pesquisar atividades"
            />
          </div>

          <div
            className="flex items-center gap-1 p-1 rounded-md border border-border bg-muted/30"
            role="group"
            aria-label="Filtrar por tipo"
          >
            {(['', ...ACTIVITY_TYPES] as const).map((t) => (
              <button
                key={t || 'all'}
                type="button"
                onClick={() => {
                  setFilterType(t);
                  setPage(0);
                }}
                aria-pressed={filterType === t}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  filterType === t
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {FILTER_LABELS[t]}
              </button>
            ))}
          </div>

          {total > 0 && (
            <span className="text-sm text-muted-foreground tabular-nums shrink-0 ml-auto">
              {total} atividade{total !== 1 ? 's' : ''}
              {isFetching && !isLoading && (
                <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
              )}
            </span>
          )}
        </div>

        <div className="card overflow-hidden">
          {isLoading && <LoadingBlock />}
          {isError && <ErrorBlock message={error?.message ?? 'Erro ao carregar atividades.'} />}

          {!isLoading && !isError && activities.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <CalendarDays className="w-7 h-7 text-muted-foreground/40" />
              </div>
              {hasFilters ? (
                <p className="text-base font-medium text-muted-foreground">
                  Nenhuma atividade corresponde à pesquisa.
                </p>
              ) : (
                <>
                  <p className="text-base font-medium text-muted-foreground">
                    Sem atividades registadas
                  </p>
                  <button
                    type="button"
                    onClick={() => setModal({ type: 'create' })}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Criar primeira atividade
                  </button>
                </>
              )}
            </div>
          )}

          {!isLoading && !isError && activities.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm" aria-label="Lista de atividades">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Nome', 'Tipo', 'Centro', 'Responsável', 'Quando', 'Sessões'].map(
                      (h) => (
                        <th
                          key={h}
                          scope="col"
                          className="px-4 py-3 text-left font-medium text-muted-foreground"
                        >
                          {h}
                        </th>
                      ),
                    )}
                    <th
                      scope="col"
                      className="px-4 py-3 w-20 text-right font-medium text-muted-foreground"
                    >
                      Acções
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {activities.map((a) => (
                    <tr
                      key={a.id}
                      className="hover:bg-accent/30 transition-colors cursor-pointer"
                      onClick={() => navigate(`/atividades/${a.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{a.name}</span>
                          {a.universo && <UniversoChip universo={a.universo} />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <TypeChip type={a.type} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{a.digitalCenterName}</td>
                      <td className="px-4 py-3 text-muted-foreground">{a.ownerName}</td>
                      <td className="px-4 py-3 text-muted-foreground tabular-nums">
                        {whenIsIt(a)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground tabular-nums">
                        {isSingleEventActivity(a.type) ? '—' : a.sessionCount}
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="flex items-center gap-1 justify-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => setModal({ type: 'edit', activity: a })}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                            aria-label={`Editar ${a.name}`}
                            title={`Editar ${a.name}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setModal({ type: 'delete', activity: a })}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            aria-label={`Arquivar ${a.name}`}
                            title={`Arquivar ${a.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <span className="text-sm text-muted-foreground tabular-nums">
                {from}–{to} de {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  aria-label="Página anterior"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>
                <span className="px-2 text-sm text-muted-foreground tabular-nums">
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  aria-label="Próxima página"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Próximo
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {modal.type === 'create' && (
        <ActivityModal
          mode="create"
          onClose={closeModal}
          onSuccess={(msg, created, sessions) => {
            toast.success(msg);
            // Go to the code. A single session has its own page; an open
            // day's code is on the activity's page, which is also where
            // several sessions, or none, are managed.
            navigate(
              sessions.length === 1 && !isSingleEventActivity(created.type)
                ? `/atividades/sessoes/${sessions[0].id}`
                : `/atividades/${created.id}`,
            );
          }}
          onError={(msg) => toast.error(msg)}
        />
      )}
      {modal.type === 'edit' && (
        <ActivityModal
          mode="edit"
          initial={modal.activity}
          onClose={closeModal}
          onSuccess={(msg) => toast.success(msg)}
          onError={(msg) => toast.error(msg)}
        />
      )}
      {modal.type === 'delete' && (
        <ConfirmDialog
          title="Arquivar Atividade"
          confirmLabel="Arquivar"
          isPending={del.isPending}
          onConfirm={() => confirmDelete(modal.activity)}
          onClose={closeModal}
        >
          Tens a certeza que queres arquivar{' '}
          <span className="font-semibold text-foreground">"{modal.activity.name}"</span>?{' '}
          {isSingleEventActivity(modal.activity.type)
            ? 'O código deixa de aceitar leituras.'
            : 'As sessões deixam de aceitar leituras.'}{' '}
          As presenças já registadas ficam guardadas.
        </ConfirmDialog>
      )}

      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
